import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { accountsQueryKey } from "./accounts";
import type { Transaction } from "./transactions";

/**
 * A transfer, as one object with two legs (`model.md` §3.5).
 *
 * The rows behind it are two ordinary transactions sharing a
 * `transfer_group_id` — which is what keeps an account ledger a plain filter and
 * the balance identity intact. The API answers in the shape the user asked in:
 * "move €500 to savings".
 */
export interface Transfer {
  id: string;
  from: Transaction;
  to: Transaction;
  /**
   * What the transfer cost in the household's base currency: what left, less
   * what arrived. Zero in one currency; across two it is the FX spread, which
   * `model.md` §6.2 says is reported rather than silently absorbed.
   */
  fx_spread_minor: number;
  cross_currency: boolean;
}

export interface CreateTransferInput {
  from_account_id: string;
  to_account_id: string;
  booked_date: string;
  /** What leaves the source account, as a positive number. */
  amount_minor: number;
  /** Required only when the two accounts use different currencies. */
  received_amount_minor?: number;
  note?: string;
}

export function useCreateTransfer(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTransferInput) =>
      apiFetch<Transfer>(`/api/v1/households/${householdId}/transfers`, {
        method: "POST",
        body: input,
      }),
    // Both balances moved and two rows joined the ledger, so both queries are
    // stale. Returned rather than discarded, so a caller that navigates on
    // success arrives at a page that already agrees with what it just did.
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ["households", householdId, "transactions"] }),
        client.invalidateQueries({ queryKey: accountsQueryKey(householdId) }),
      ]),
  });
}
